/**
 * useVehicleActivityLog — Histórico de auditoria de um veículo
 *
 * Lê os eventos gravados em `vehicle_activity_log` via RPC
 * `get_vehicle_activity_log` (SECURITY DEFINER), espelhando a mesma
 * estrutura do histórico de tarefas (useTaskActivityLog).
 *
 * @version 1.0.0
 * RELEASE: 2026-08-09 v1.0.0 - Criação inicial (aba Histórico do modal de veículo)
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';

const logger = createLogger('useVehicleActivityLog');

/** Item da timeline de auditoria do veículo */
export interface VehicleActivityLogEntry {
  id: string;
  vehicle_id: string;
  action: string;
  performed_by: string | null;
  performer_name: string | null;
  performer_avatar: string | null;
  changes: Record<string, { from?: unknown; to?: unknown; changed?: boolean }>;
  metadata: Record<string, unknown>;
  /** Dicionário UUID → nome (negócio, tarefa, contrato, cliente, contato) */
  labels?: Record<string, string>;
  created_at: string;
}

/**
 * Histórico completo de alterações do veículo.
 */
export function useVehicleActivityLog(vehicleId: string | null, options?: { enabled?: boolean }) {
  const { enabled = true } = options || {};
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id ?? null;

  const query = useQuery({
    queryKey: ['vehicle-activity-log', vehicleId, tenantId],
    enabled: enabled && !!vehicleId && !!tenantId,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    queryFn: async (): Promise<VehicleActivityLogEntry[]> => {
      if (!vehicleId) return [];

      const { data, error } = await (supabase.rpc as any)('get_vehicle_activity_log', {
        p_vehicle_id: vehicleId,
        p_tenant_hint: tenantId,
      });

      if (error) {
        logger.error('Falha ao carregar histórico do veículo', { error: error.message });
        throw error;
      }

      return (data || []) as VehicleActivityLogEntry[];
    },
  });

  return {
    logs: query.data || [],
    isLoading: query.isLoading,
    error: query.error as Error | null,
    refetch: query.refetch,
  };
}
